/**
 * Foundation Data v2 extracted rows, grouped by the extraction that produced them.
 *
 * Since ESG-3992 an extraction belongs to its source document, so the panel shows one entry per run and
 * expanding it reveals what that document said. The rows are already fetched for the year and each carries
 * its `runId`, so this is a group-by rather than a request per file — a per-run endpoint would be the
 * escalation if a year ever holds enough rows for that to matter.
 */

/**
 * `runId` -> that run's rows, in the order the server sent them.
 *
 * Rows with no `runId` are collected under `null`. They exist: before ESG-3992 a row could be added by
 * hand, and those carry no run. They belong to no document, so a caller showing per-file groups should
 * either label them separately or leave them out — silently filing them under some file would be worse
 * than either.
 */
export function groupRowsByRun(rows) {
  const byRun = new Map();
  if (!Array.isArray(rows)) return byRun;
  rows.forEach((row) => {
    if (!row) return;
    // `?? null` rather than `|| null`: a runId of 0 is not a real id here, but relying on falsiness to
    // decide that is how a legitimate id would one day be dropped.
    const key = row.runId ?? null;
    const existing = byRun.get(key);
    if (existing) {
      existing.push(row);
    } else {
      byRun.set(key, [row]);
    }
  });
  return byRun;
}

/** How many rows one run produced, without building the whole map. */
export function countRowsForRun(rows, runId) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((row) => row && row.runId === runId).length;
}
